import { useCallback, useEffect, useState } from 'react';
import { Trash2, UserPlus, X } from 'lucide-react';
import { apiPersonManagers, apiPersons } from '../api/client.js';
import Avatar from './Avatar';
import PeoplePicker from './PeoplePicker';

/**
 * Sprint 9 — Section "Hiérarchie" affichée dans la modale d'édition d'une
 * Person. Liste les managers actifs avec bouton de retrait, expose un picker
 * pour en ajouter, et affiche les subordonnés directs en lecture seule.
 *
 * Réservée aux ADMIN (`canEdit=true`) ; les autres rôles voient les chips en
 * lecture seule. Tous les appels API sont best-effort : un échec ne casse pas
 * l'édition de la Person principale (la section est indépendante).
 */
export default function PersonManagersSection({ personId, canEdit = false }) {
  const [managers, setManagers] = useState([]);
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showPicker, setShowPicker] = useState(false);
  const [busyId, setBusyId] = useState(null);

  const reload = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [m, r] = await Promise.all([
        apiPersonManagers.listManagers(personId),
        apiPersonManagers.listReports(personId),
      ]);
      setManagers(m.items ?? []);
      setReports(r.items ?? []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [personId]);

  useEffect(() => {
    if (!personId) return;
    reload();
  }, [personId, reload]);

  const fetchOptions = useCallback(
    async (q) => {
      const { items } = await apiPersons.list('active');
      const lower = (q ?? '').toLowerCase().trim();
      const filtered = items
        .filter((p) => p.id !== personId)
        .filter((p) => !managers.some((m) => m.id === p.id))
        .filter((p) => {
          if (!lower) return true;
          const hay = `${p.firstName} ${p.lastName} ${p.email ?? ''}`.toLowerCase();
          return hay.includes(lower);
        })
        .slice(0, 30)
        .map((p) => ({
          id: p.id,
          fullName: `${p.firstName} ${p.lastName}`,
          firstName: p.firstName,
          lastName: p.lastName,
          jobTitle: p.jobTitle,
          avatarUrl: p.avatarUrl,
        }));
      return filtered;
    },
    [personId, managers],
  );

  const onAssign = async (opt) => {
    setBusyId(opt.id);
    setError(null);
    try {
      await apiPersonManagers.assign(personId, opt.id);
      setShowPicker(false);
      await reload();
    } catch (err) {
      setError(err.message);
    } finally {
      setBusyId(null);
    }
  };

  const onRemove = async (managerId) => {
    if (!window.confirm('Retirer ce manager ?')) return;
    setBusyId(managerId);
    setError(null);
    try {
      await apiPersonManagers.remove(personId, managerId);
      await reload();
    } catch (err) {
      setError(err.message);
    } finally {
      setBusyId(null);
    }
  };

  const sectionTitle = {
    margin: '4px 0 6px',
    fontSize: 12,
    fontWeight: 600,
    letterSpacing: '0.04em',
    textTransform: 'uppercase',
    color: 'var(--text-muted)',
  };

  const chipStyle = {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 8,
    padding: '4px 8px 4px 4px',
    background: 'var(--glass)',
    border: '1px solid var(--glass-border)',
    borderRadius: 'var(--radius-pill)',
    fontSize: 13,
    color: 'var(--text)',
  };

  const removeBtn = {
    background: 'transparent',
    border: 'none',
    color: 'var(--text-muted)',
    cursor: 'pointer',
    display: 'inline-flex',
    alignItems: 'center',
    padding: 0,
  };

  const addBtn = {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    padding: '6px 12px',
    background: 'transparent',
    color: 'var(--accent)',
    border: '1px dashed var(--glass-border)',
    borderRadius: 'var(--radius-pill)',
    fontSize: 13,
    cursor: 'pointer',
  };

  return (
    <div>
      <div style={sectionTitle}>Hiérarchie</div>

      {error ? <div style={{ color: 'var(--danger)', fontSize: 12, marginBottom: 8 }}>{error}</div> : null}

      {/* Managers */}
      <div style={{ marginBottom: 12 }}>
        <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 6 }}>
          Managers
        </div>
        {loading ? (
          <div style={{ fontSize: 13, color: 'var(--text-muted)' }}>Chargement…</div>
        ) : managers.length === 0 ? (
          <div style={{ fontSize: 13, color: 'var(--text-muted)', fontStyle: 'italic' }}>
            Aucun manager affecté
          </div>
        ) : (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 8 }}>
            {managers.map((m) => (
              <span key={m.id} style={chipStyle}>
                <Avatar
                  person={{ firstName: m.firstName, lastName: m.lastName, avatarUrl: m.avatarUrl }}
                  size={20}
                />
                {m.firstName} {m.lastName}
                {canEdit ? (
                  <button
                    type="button"
                    onClick={() => onRemove(m.id)}
                    disabled={busyId === m.id}
                    title="Retirer ce manager"
                    style={removeBtn}
                  >
                    <X size={14} />
                  </button>
                ) : null}
              </span>
            ))}
          </div>
        )}

        {canEdit ? (
          showPicker ? (
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8, marginTop: 4 }}>
              <div style={{ flex: 1, minWidth: 280 }}>
                <PeoplePicker
                  fetchOptions={fetchOptions}
                  onSelect={onAssign}
                  placeholder="Rechercher une personne…"
                  autoFocus
                  emptyMessage="Aucune personne disponible"
                />
              </div>
              <button
                type="button"
                onClick={() => setShowPicker(false)}
                style={{
                  background: 'transparent',
                  border: '1px solid var(--glass-border)',
                  color: 'var(--text-muted)',
                  borderRadius: 'var(--radius-sm)',
                  padding: '6px 10px',
                  fontSize: 13,
                  cursor: 'pointer',
                }}
              >
                Annuler
              </button>
            </div>
          ) : (
            <button type="button" onClick={() => setShowPicker(true)} style={addBtn}>
              <UserPlus size={14} /> Ajouter un manager
            </button>
          )
        ) : null}
      </div>

      {/* Subordonnés directs */}
      <div>
        <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 6 }}>
          Subordonnés directs <span style={{ opacity: 0.6 }}>({reports.length})</span>
        </div>
        {loading ? (
          <div style={{ fontSize: 13, color: 'var(--text-muted)' }}>Chargement…</div>
        ) : reports.length === 0 ? (
          <div style={{ fontSize: 13, color: 'var(--text-muted)', fontStyle: 'italic' }}>
            Aucun subordonné direct
          </div>
        ) : (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
            {reports.map((r) => (
              <span key={r.id} style={chipStyle}>
                <Avatar
                  person={{ firstName: r.firstName, lastName: r.lastName, avatarUrl: r.avatarUrl }}
                  size={20}
                />
                {r.firstName} {r.lastName}
              </span>
            ))}
          </div>
        )}
      </div>

      {/* Trash2 imported but unused — keep available for future "supprimer tous mes managers" action. */}
      <span style={{ display: 'none' }}>
        <Trash2 size={1} />
      </span>
    </div>
  );
}
